import React, { useState, useEffect, useRef, MouseEvent } from 'react'
import LZString from 'lz-string'
import { STORAGE, STORAGE_NAME } from '../../redux/persistState'
import store from '../../redux/store'
import {
  Container,
  Header,
  Title,
  CloseButton,
  TabsContainer,
  Tab,
  Content,
  SearchBox,
  StateField,
  ExpandButton,
  TreeContainer,
  Footer,
  FooterRow,
  FooterButton,
  ErrorMessage,
} from './ReduxStateDebugger.styles'

// Create unique channel ID for tab communication
const CHANNEL_ID = 'redux_state_debugger_channel'

// Standalone page HTML template
const generateStandalonePage = (initialState: any): string => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Redux State Debugger</title>
  <style>
    body { 
      font-family: monospace; 
      margin: 0; 
      padding: 20px; 
      background: #f8f8f8;
      font-size: 14px;
    }
    .container { 
      max-width: 100%; 
      margin: 0 auto; 
      background: white; 
      border: 1px solid #ccc; 
      box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
      border-radius: 4px; 
      overflow: hidden;
      display: flex;
      flex-direction: column;
      height: calc(100vh - 40px);
    }
    .header { 
      padding: 12px 20px; 
      background: #f0f0f0; 
      border-bottom: 1px solid #ccc;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .title { 
      margin: 0; 
      font-size: 16px;
    }
    .content { 
      padding: 20px; 
      overflow: auto; 
      flex-grow: 1;
    }
    .search {
      width: 100%;
      padding: 8px;
      margin-bottom: 16px;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-sizing: border-box;
    }
    .tree-item {
      margin: 2px 0;
      padding: 4px;
      border-radius: 2px;
    }
    .tree-item:hover {
      background-color: #f5f5f5;
    }
    .tree-row {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
    .tree-children {
      margin-left: 20px;
    }
    .expand-button {
      display: inline-block;
      width: 12px;
      height: 12px;
      text-align: center;
      margin-right: 4px;
    }
    .key {
      font-weight: bold;
      margin-right: 4px;
    }
    .string-value {
      color: #188038;
    }
    .number-value {
      color: #1967d2;
    }
    .boolean-value {
      color: #c5221f;
    }
    .null-value {
      color: #666;
      font-style: italic;
    }
    .object-value {
      color: #80868b;
    }
    .array-value {
      color: #80868b;
    }
    .status {
      padding: 8px 20px;
      background: #f0f0f0;
      border-top: 1px solid #ccc;
      font-size: 12px;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="title">Redux State Debugger</h1>
      <div>
        <span id="status">Disconnected</span>
      </div>
    </div>
    <div class="content">
      <input type="text" id="searchInput" class="search" placeholder="Filter by key...">
      <div id="stateContent"></div>
    </div>
  </div>

  <script>
    // Initial state
    let state = ${JSON.stringify(initialState)};
    let filter = '';
    const channelID = '${CHANNEL_ID}';
    
    // Set up BroadcastChannel for receiving state updates
    const channel = new BroadcastChannel(channelID);
    
    channel.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'STATE_UPDATE') {
        state = event.data.payload;
        renderState();
        document.getElementById('status').textContent = 'Updated: ' + new Date().toLocaleTimeString();
      }
    });
    
    const expandedPaths = new Set();
    
    function toggleNode(path) {
      if (expandedPaths.has(path)) {
        expandedPaths.delete(path);
      } else {
        expandedPaths.add(path);
      }
      renderState();
    }
    
    function formatValue(value) {
      if (value === null) return '<span class="null-value">null</span>';
      if (value === undefined) return '<span class="null-value">undefined</span>';
      
      switch (typeof value) {
        case 'string':
          return '<span class="string-value">"' + value.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '"</span>';
        case 'number':
          return '<span class="number-value">' + value + '</span>';
        case 'boolean':
          return '<span class="boolean-value">' + value + '</span>';
        case 'object':
          if (Array.isArray(value)) {
            return '<span class="array-value">Array(' + value.length + ')</span>';
          } else {
            return '<span class="object-value">Object(' + Object.keys(value).length + ')</span>';
          }
        default:
          return '<span>' + String(value) + '</span>';
      }
    }
    
    function renderNode(key, value, path = '') {
      const currentPath = path ? path + '.' + key : key;
      const isObject = value !== null && typeof value === 'object';
      const isExpanded = expandedPaths.has(currentPath);
      
      if (filter && !currentPath.toLowerCase().includes(filter.toLowerCase())) {
        return '';
      }
      
      let html = '<div class="tree-item">';
      
      html += '<div class="tree-row" data-path="' + currentPath + '" onclick="handleRowClick(event)">';
      
      if (isObject) {
        html += '<span class="expand-button">' + (isExpanded ? '▼' : '►') + '</span>';
      } else {
        html += '<span class="expand-button"></span>';
      }
      
      html += '<span class="key">' + key + ':</span> ';
      html += formatValue(value);
      html += '</div>';
      
      if (isObject && isExpanded) {
        html += '<div class="tree-children">';
        
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            html += renderNode(index.toString(), item, currentPath);
          });
        } else {
          Object.keys(value).forEach(childKey => {
            html += renderNode(childKey, value[childKey], currentPath);
          });
        }
        
        html += '</div>';
      }
      
      html += '</div>';
      return html;
    }
    
    window.handleRowClick = function(event) {
      const path = event.currentTarget.getAttribute('data-path');
      const value = getValueByPath(state, path);
      
      if (value !== null && typeof value === 'object') {
        toggleNode(path);
        event.stopPropagation();
      }
    };
    
    function getValueByPath(obj, path) {
      const parts = path.split('.');
      let current = obj;
      
      for (const part of parts) {
        if (current === null || current === undefined) {
          return undefined;
        }
        
        if (!isNaN(parseInt(part))) {
          current = current[parseInt(part)];
        } else {
          current = current[part];
        }
      }
      
      return current;
    }
    
    // Render the state
    function renderState() {
      const stateContent = document.getElementById('stateContent');
      
      let html = '';
      Object.keys(state).forEach(key => {
        html += renderNode(key, state[key]);
      });
      
      stateContent.innerHTML = html;
    }
    
    document.getElementById('searchInput').addEventListener('input', (e) => {
      filter = e.target.value;
      renderState();
    });
    
    renderState();
    document.getElementById('status').textContent = 'Connected at ' + new Date().toLocaleTimeString();
    
    channel.postMessage({ type: 'TAB_READY' });
  </script>
</body>
</html>
  `
}

const JsonTree = ({ data, path = '', filter = '', expanded = {} }: any) => {
  const [expandedNodes, setExpandedNodes] = useState(expanded)

  const toggleNode = (nodePath: string) => {
    setExpandedNodes({
      ...expandedNodes,
      [nodePath]: !expandedNodes[nodePath],
    })
  }

  if (typeof data !== 'object' || data === null) {
    return (
      <TreeContainer $indented={true}>
        <span>{JSON.stringify(data)}</span>
      </TreeContainer>
    )
  }

  return (
    <TreeContainer $indented={!!path}>
      {Object.entries(data).map(([key, value]) => {
        const currentPath = path ? `${path}.${key}` : key
        const isObject = typeof value === 'object' && value !== null
        const isExpanded = expandedNodes[currentPath]

        if (filter && !currentPath.toLowerCase().includes(filter.toLowerCase())) {
          return null
        }

        return (
          <div key={currentPath}>
            <StateField
              $isExpanded={isExpanded}
              onClick={() => isObject && toggleNode(currentPath)}
            >
              {isObject && (
                <ExpandButton>
                  {isExpanded ? '▼' : '►'}
                </ExpandButton>
              )}
              <strong>{key}:</strong> {isObject
                ? (Array.isArray(value)
                  ? `Array(${(value as any[]).length})`
                  : `Object(${Object.keys(value).length})`)
                : JSON.stringify(value)
              }
            </StateField>

            {isObject && isExpanded && (
              <JsonTree
                data={value}
                path={currentPath}
                filter={filter}
                expanded={expandedNodes}
              />
            )}
          </div>
        )
      })}
    </TreeContainer>
  )
}

type ReduxStateDebuggerProps = {
  onClose: () => void
}

const ReduxStateDebugger: React.FC<ReduxStateDebuggerProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('live')
  const [filter, setFilter] = useState('')
  const [liveState, setLiveState] = useState<any>(store.getState())
  const [sessionStorageState, setSessionStorageState] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [externalTab, setExternalTab] = useState<Window | null>(null)

  const [position, setPosition] = useState({ x: 20, y: 20 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const debuggerRef = useRef<HTMLDivElement>(null)

  // Create broadcast channel for tab communication
  const [channel, setChannel] = useState<BroadcastChannel | null>(null)

  useEffect(() => {
    // Create BroadcastChannel for cross-tab communication
    const newChannel = new BroadcastChannel(CHANNEL_ID)
    setChannel(newChannel)

    // Listen for messages from external tab
    newChannel.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'TAB_READY') {
        // External tab is ready, send current state
        if (externalTab && !externalTab.closed) {
          newChannel.postMessage({
            type: 'STATE_UPDATE',
            payload: liveState,
          })
        }
      }
    })

    return () => {
      newChannel.close()
    }
  }, [liveState, externalTab])

  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const newState = store.getState()
      setLiveState(newState)

      if (channel && externalTab && !externalTab.closed) {
        channel.postMessage({
          type: 'STATE_UPDATE',
          payload: newState,
        })
      }
    })

    return () => unsubscribe()
  }, [channel, externalTab])

  // Load state from session storage
  useEffect(() => {
    try {
      const compressed = STORAGE.getItem(STORAGE_NAME)
      if (compressed) {
        const raw = LZString.decompressFromUTF16(compressed)
        if (raw) {
          setSessionStorageState(JSON.parse(raw))
        } else {
          setError('Failed to decompress state')
        }
      } else {
        setError('No state found in session storage')
      }
    } catch (e) {
      setError(`Error loading state: ${e}`)
    }
  }, [activeTab])

  const handleMouseDown = (e: MouseEvent) => {
    if (debuggerRef.current) {
      setIsDragging(true)
      const rect = debuggerRef.current.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      })
    }
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove as any)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove as any)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging])

  const handleCopyState = () => {
    const stateToCopy = activeTab === 'live' ? liveState : sessionStorageState
    if (stateToCopy) {
      navigator.clipboard.writeText(JSON.stringify(stateToCopy, null, 2))
        .then(() => alert('State copied to clipboard'))
        .catch(err => alert(`Failed to copy: ${err}`))
    }
  }

  const handleClearState = () => {
    if (confirm('Are you sure you want to clear the persisted state?')) {
      STORAGE.removeItem(STORAGE_NAME)
      setSessionStorageState(null)
      setError('State cleared from session storage')
    }
  }

  const handleOpenInNewTab = () => {
    // Generate HTML content
    const htmlContent = generateStandalonePage(liveState)

    // Create Blob with HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const url = URL.createObjectURL(blob)

    // Open new tab with the content
    const newTab = window.open(url, '_blank')
    if (newTab) {
      setExternalTab(newTab)

      // Clean up the URL when the tab is closed
      newTab.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url)
        setExternalTab(null)
      })
    }
  }

  return (
    <Container
      ref={debuggerRef}
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
        right: 'auto',
      }}
    >
      <Header onMouseDown={handleMouseDown}>
        <Title>Redux State Debugger</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <TabsContainer>
        <Tab
          $isActive={activeTab === 'live'}
          onClick={() => setActiveTab('live')}
        >
          Live State
        </Tab>
        <Tab
          $isActive={activeTab === 'storage'}
          onClick={() => setActiveTab('storage')}
        >
          Session Storage
        </Tab>
      </TabsContainer>

      <Content>
        <SearchBox
          type="text"
          placeholder="Filter by key..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        {activeTab === 'live' ? (
          <JsonTree data={liveState} filter={filter} />
        ) : (
          error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            sessionStorageState && <JsonTree data={sessionStorageState} filter={filter} />
          )
        )}
      </Content>

      <Footer>
        <FooterRow>
          <FooterButton onClick={handleCopyState}>
            Copy to Clipboard
          </FooterButton>

          <FooterButton onClick={handleOpenInNewTab}>
            Open in New Tab
          </FooterButton>
        </FooterRow>

        {activeTab === 'storage' && (
          <FooterRow>
            <FooterButton
              $isWarning
              onClick={handleClearState}
            >
              Clear Storage
            </FooterButton>
          </FooterRow>
        )}
      </Footer>
    </Container>
  )
}

export default ReduxStateDebugger
